import axios from 'axios';
import { useUserStore } from '../store/userStore';
import {
  User,
  UserCreate,
  MembershipPass,
  MembershipPassCreate,
  Seat,
  SeatFilter,
  SeatListResponse,
  SeatReservation,
  SeatReservationCreate,
  CheckInRequest,
  CheckInResponse,
  CheckOutResponse,
  RealTimeStatsResponse,
  ZoneMovementCreate,
  ZoneMovementResponse,
  PaginationParams,
  PaymentPlan,
  PaymentRequest,
  PaymentResponse,
  PaymentHistory,
  RankingListResponse,
  PersonalStats,
  LeaderboardResponse,
  RankingSummary,
} from '../types';
import { API_BASE_URL, API_TIMEOUT, WS_BASE_URL } from '../config/api';
import { apiClient } from './authApi';

// 기존 Axios 인스턴스를 authApi의 apiClient로 교체
const api = apiClient;

// 사용자 관련 API
export const userApi = {
  // 사용자 생성
  create: (userData: UserCreate): Promise<User> =>
    api.post('/users', userData).then((res) => res.data),

  // 사용자 조회
  getById: (userId: string): Promise<User> =>
    api.get(`/users/${userId}`).then((res) => res.data),

  // 사용자 정보 수정
  update: (userId: string, userData: Partial<UserCreate>): Promise<User> =>
    api.put(`/users/${userId}`, userData).then((res) => res.data),

  // 사용자 이용권 조회
  getMembershipPasses: (userId: string): Promise<MembershipPass[]> =>
    api.get(`/users/${userId}/membership-passes`).then((res) => res.data),

  // 사용자 좌석 예약
  reserveSeat: (
    userId: string,
    reservationData: SeatReservationCreate
  ): Promise<SeatReservation> =>
    api
      .post(`/users/${userId}/reservations`, reservationData)
      .then((res) => res.data),

  // 체크인
  checkIn: (
    userId: string,
    checkInData: CheckInRequest
  ): Promise<CheckInResponse> =>
    api.post(`/users/${userId}/check-in`, checkInData).then((res) => res.data),

  // 사용자 통계 조회
  getStats: (userId: string) =>
    api.get(`/users/${userId}/stats`).then((res) => res.data),
};

// 이용권 관련 API
export const membershipApi = {
  // 이용권 구매
  create: (passData: MembershipPassCreate): Promise<MembershipPass> =>
    api.post('/membership-passes', passData).then((res) => res.data),
};

// 좌석 관련 API
export const seatApi = {
  // 좌석 목록 조회
  getAll: (
    filters?: SeatFilter,
    pagination?: PaginationParams
  ): Promise<SeatListResponse> => {
    const params = new URLSearchParams();

    // org_id 추가 - 사용자의 organization_id 사용
    const userStore = useUserStore.getState();
    const orgId = userStore.currentUser?.org_id || userStore.currentUser?.business_id || '1';
    params.append('org_id', orgId.toString());

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    if (pagination) {
      params.append('page', pagination.page.toString());
      params.append('size', pagination.size.toString());
    }

    return api.get(`/seats?${params.toString()}`).then((res) => res.data);
  },

  // 특정 좌석 조회
  getById: (seatNumber: number): Promise<Seat> =>
    api.get(`/seats/${seatNumber}`).then((res) => res.data),

  // 좌석 상태 변경 (오너 전용)
  updateStatus: (seatNumber: number, status: string): Promise<any> =>
    api.put(`/seats/${seatNumber}/status`, { status }).then((res) => res.data),
};

// 예약 관련 API
export const reservationApi = {
  // 예약 취소
  cancel: (
    reservationId: string
  ): Promise<{ success: boolean; message: string }> =>
    api.delete(`/reservations/${reservationId}`).then((res) => res.data),
};

// 세션 관련 API
export const sessionApi = {
  // 체크아웃
  checkOut: (sessionId: string): Promise<CheckOutResponse> =>
    api.post(`/sessions/${sessionId}/check-out`).then((res) => res.data),

  // 실시간 통계 조회
  getRealTimeStats: (sessionId: string): Promise<RealTimeStatsResponse> =>
    api.get(`/sessions/${sessionId}/real-time-stats`).then((res) => res.data),

  // 구역 이동 기록
  recordZoneMovement: (
    sessionId: string,
    movementData: ZoneMovementCreate
  ): Promise<ZoneMovementResponse> =>
    api
      .post(`/sessions/${sessionId}/zone-movement`, movementData)
      .then((res) => res.data),
};

// 통계 관련 API
export const statsApi = {
  // 일일 통계 조회
  getDailyStats: (startDate?: string, endDate?: string) =>
    api
      .get('/stats/daily', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((res) => res.data),
};

// WebSocket 연결 관리
export class WebSocketService {
  private ws: WebSocket | null = null;
  private clientId: string;
  private listeners: Map<string, ((data: any) => void)[]> = new Map();

  constructor(clientId: string) {
    this.clientId = clientId;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
                this.ws = new WebSocket(`${WS_BASE_URL}/ws/${this.clientId}`);

        this.ws.onopen = () => {
          if (process.env.NODE_ENV === 'development') {
          }
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Failed to parse WebSocket message:', error);
            }
          }
        };

        this.ws.onclose = () => {
          if (process.env.NODE_ENV === 'development') {
          }
          this.ws = null;
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  private handleMessage(message: any): void {
    const { type, data } = message;
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  sendMessage(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  ping(): void {
    this.sendMessage('ping');
  }
}

// 결제 관련 API
export const paymentApi = {
  // 이용권 요금제 조회
  getPlans: (): Promise<PaymentPlan[]> =>
    api.get('/payment/plans').then((res) => res.data),

  // 결제 처리
  processPayment: (
    userId: string,
    paymentData: PaymentRequest
  ): Promise<PaymentResponse> =>
    api.post(`/users/${userId}/payments`, paymentData).then((res) => res.data),

  // 결제 내역 조회
  getPaymentHistory: (userId: string): Promise<PaymentHistory[]> =>
    api.get(`/users/${userId}/payments`).then((res) => res.data),
};

// 오너 대시보드 API
export const ownerApi = {
  // 오너 대시보드 통계 조회
  getDashboardStats: (): Promise<{
    today_revenue: number;
    month_revenue: number;
    average_session_time: number;
    active_users: number;
    total_payments_today: number;
    total_payments_month: number;
    last_updated: string;
  }> => api.get('/v1/owner/dashboard/stats').then((res) => {
    // API 응답 형식을 컴포넌트에서 기대하는 형식으로 변환
    if (res.data && res.data.revenue_stats) {
      return {
        today_revenue: res.data.revenue_stats.today || 0,
        month_revenue: res.data.revenue_stats.this_week || 0,
        average_session_time: res.data.time_stats?.average_session_time || 0,
        active_users: res.data.seat_stats?.occupied || 0,
        total_payments_today: Math.floor((res.data.revenue_stats.today || 0) / 5000), // 매출 / 평균 이용료
        total_payments_month: Math.floor((res.data.revenue_stats.this_week || 0) / 5000),
        last_updated: new Date().toISOString(),
      };
    }
    return res.data;
  }),

  // 회원 목록 조회
  getMembers: (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    members: Array<{
      id: string;
      name: string;
      email: string;
      phone: string;
      memberNumber: string;
      user_id?: string;
      member_id?: string;
    }>;
    total: number;
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    return api.get(`/v1/owner/members?${queryParams}`).then((res) => res.data);
  },

  // 회원 생성
  createMember: (memberData: {
    name: string;
    phone: string;
    email?: string;
    memberNumber?: string;
    membershipType?: string;
  }): Promise<{
    id: string;
    name: string;
    phone: string;
    email: string;
    memberNumber: string;
    user_id?: string;
    member_id?: string;
  }> => {
    return api.post('/v1/owner/members', memberData).then((res) => res.data);
  },
};

// 출결 관리 API
export const attendanceApi = {
  // 현재 이용자 목록 조회
  getCurrentUsers: (
    orgId: number = 1
  ): Promise<
    {
      user_id: string;
      user_name: string;
      seat_number: number;
      check_in_time: string;
      elapsed_time_minutes: number;
      membership_type: string;
      remaining_hours?: number;
      status: string;
    }[]
  > => api.get(`/attendance/current?org_id=${orgId}`).then((res) => res.data),

  // 일일 출결 기록 조회
  getDailyAttendance: (
    orgId: number = 1,
    date?: string
  ): Promise<
    {
      user_id: string;
      user_name: string;
      seat_number: number;
      check_in_time: string;
      check_out_time?: string;
      total_minutes?: number;
      membership_type: string;
      status: string;
    }[]
  > => {
    const params = new URLSearchParams({ org_id: orgId.toString() });
    if (date) params.append('date', date);
    return api.get(`/attendance/daily?${params}`).then((res) => res.data);
  },

  // 출결 통계 조회
  getStats: (
    orgId: number = 1,
    startDate?: string,
    endDate?: string
  ): Promise<{
    total_users: number;
    current_users: number;
    today_checkins: number;
    average_session_minutes: number;
    peak_hours: Array<{
      hour: number;
      user_count: number;
      hour_display: string;
    }>;
    membership_breakdown: Record<string, number>;
    last_updated: string;
  }> => {
    const params = new URLSearchParams({ org_id: orgId.toString() });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/attendance/stats?${params}`).then((res) => res.data);
  },

  // 출결 요약 정보 조회
  getSummary: (
    orgId: number = 1
  ): Promise<{
    org_id: number;
    current_time: string;
    summary: {
      total_seats: number;
      occupied_seats: number;
      available_seats: number;
      utilization_rate: number;
    };
    today_overview: {
      total_checkins: number;
      total_checkouts: number;
      active_sessions: number;
      peak_time: string;
    };
  }> => api.get(`/attendance/summary?org_id=${orgId}`).then((res) => res.data),
};

// 순공부 순위 API
export const rankingsApi = {
  getStudyRankings: (params: {
    orgId: number;
    period?: string;
    group?: string;
    groupValue?: string;
    limit?: number;
    offset?: number;
    currentUserId?: string;
  }): Promise<RankingListResponse> => {
    const queryParams = new URLSearchParams({
      org_id: params.orgId.toString(),
      period: params.period || 'daily',
      group: params.group || 'overall',
      limit: (params.limit || 10).toString(),
      offset: (params.offset || 0).toString(),
    });

    if (params.groupValue) queryParams.append('group_value', params.groupValue);
    if (params.currentUserId)
      queryParams.append('current_user_id', params.currentUserId);

    return api.get(`/rankings/study?${queryParams}`).then((res) => res.data);
  },

  getUserStats: (
    userId: string,
    params: {
      orgId: number;
      period?: string;
    }
  ): Promise<PersonalStats> => {
    const queryParams = new URLSearchParams({
      org_id: params.orgId.toString(),
      period: params.period || 'daily',
    });

    return api
      .get(`/rankings/users/${userId}/study-stats?${queryParams}`)
      .then((res) => res.data);
  },

  getLeaderboard: (params: {
    orgId: number;
    period?: string;
    topN?: number;
  }): Promise<LeaderboardResponse> => {
    const queryParams = new URLSearchParams({
      org_id: params.orgId.toString(),
      period: params.period || 'daily',
      top_n: (params.topN || 3).toString(),
    });

    return api
      .get(`/rankings/leaderboard?${queryParams}`)
      .then((res) => res.data);
  },

  getRankingSummary: (orgId: number): Promise<RankingSummary> => {
    return api.get(`/rankings/summary?org_id=${orgId}`).then((res) => res.data);
  },
};

export default api;
